import type { QueryClient } from "@tanstack/react-query";
import type { OrgProjectContextOption, OrgProjectLoader } from "./types";

const CONTEXT_STALE_TIME_MS = 5 * 60 * 1000;

type CoordinatorListResponse<T> = {
  data?: {
    items?: T[];
  };
};

type CoordinatorOrganization = {
  organization_id: string;
  name: string;
  description?: string | null;
  is_default: boolean;
  created_at?: string;
  /** Whether the caller may edit this org; mirrors the org metadata update gate. */
  can_edit?: boolean;
};

type CoordinatorProject = {
  project_id: string;
  name: string;
  description?: string | null;
  is_default: boolean;
  created_at?: string;
  can_edit?: boolean | null;
};

class CoordinatorHttpError extends Error {
  status: number;
  data: unknown;

  constructor(response: Response, data: unknown) {
    const message =
      typeof data === "object" &&
      data !== null &&
      "message" in data &&
      typeof data.message === "string"
        ? data.message
        : `Request failed with status ${response.status}`;

    super(message);
    this.name = "CoordinatorHttpError";
    this.status = response.status;
    this.data = data;
  }
}

function isAuthError(error: unknown): boolean {
  return error instanceof CoordinatorHttpError && error.status === 401;
}

type CreateCoordinatorQueriesOptions = {
  baseUrl: string;
  /** Called when a query returns 401. Receives the thrown error. */
  onAuthError?: (error: unknown) => void;
  /** Optional fetch override. */
  fetch?: typeof globalThis.fetch;
};

function createCoordinatorQueriesFromOptions({
  baseUrl,
  onAuthError,
  fetch: customFetch,
}: CreateCoordinatorQueriesOptions) {
  const doFetch: typeof globalThis.fetch = customFetch ?? globalThis.fetch;

  async function fetchJson<T>(url: string): Promise<T> {
    const response = await doFetch(url, { credentials: "include" });
    const text = await response.text();

    if (!response.ok) {
      let data: unknown;
      try {
        data = text ? JSON.parse(text) : undefined;
      } catch {
        data = text || undefined;
      }
      throw new CoordinatorHttpError(response, data);
    }

    if (!text) {
      throw new Error(`Empty response body from ${url}`);
    }

    return JSON.parse(text) as T;
  }

  const orgProjectKeys = {
    all: (scopeKey?: string | null) =>
      ["org-project-context", baseUrl, scopeKey?.trim() || "global"] as const,
    organizations: (scopeKey?: string | null) =>
      [...orgProjectKeys.all(scopeKey), "organizations"] as const,
    projects: (
      scopeKey: string | null | undefined,
      organizationId: string | null | undefined
    ) => [...orgProjectKeys.all(scopeKey), "projects", organizationId] as const,
  };

  async function withAuthGuard<T>(fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (error: unknown) {
      if (isAuthError(error)) {
        onAuthError?.(error);
      }
      throw error;
    }
  }

  function fetchOrganizations(): Promise<OrgProjectContextOption[]> {
    return withAuthGuard(async () => {
      const data = await fetchJson<
        CoordinatorListResponse<CoordinatorOrganization>
      >(`${baseUrl}/api/coordinator/orgs`);
      return (data.data?.items ?? []).map((o) => ({
        id: o.organization_id,
        name: o.name,
        description: o.description ?? null,
        isDefault: o.is_default,
        createdAt: o.created_at,
        canEdit: o.can_edit,
      }));
    });
  }

  function fetchProjects(
    organizationId: string
  ): Promise<OrgProjectContextOption[]> {
    return withAuthGuard(async () => {
      const data = await fetchJson<CoordinatorListResponse<CoordinatorProject>>(
        `${baseUrl}/api/coordinator/orgs/${encodeURIComponent(organizationId)}/projects`
      );
      return (data.data?.items ?? []).map((p) => ({
        id: p.project_id,
        name: p.name,
        description: p.description ?? null,
        isDefault: p.is_default,
        createdAt: p.created_at,
        canEdit: p.can_edit ?? undefined,
      }));
    });
  }

  function getOrganizationsQueryOptions(scopeKey?: string | null) {
    return {
      queryKey: orgProjectKeys.organizations(scopeKey),
      queryFn: fetchOrganizations,
      staleTime: CONTEXT_STALE_TIME_MS,
    };
  }

  function getProjectsQueryOptions(
    organizationId: string,
    scopeKey?: string | null
  ) {
    return {
      queryKey: orgProjectKeys.projects(scopeKey, organizationId),
      queryFn: () => fetchProjects(organizationId),
      staleTime: CONTEXT_STALE_TIME_MS,
    };
  }

  function createLoader(
    queryClient: QueryClient,
    scopeKey: string | null
  ): OrgProjectLoader {
    return {
      listOrganizations: () =>
        queryClient.fetchQuery(getOrganizationsQueryOptions(scopeKey)),
      listProjects: (organizationId) =>
        queryClient.fetchQuery(
          getProjectsQueryOptions(organizationId, scopeKey)
        ),
    };
  }

  return {
    orgProjectKeys,
    getOrganizationsQueryOptions,
    getProjectsQueryOptions,
    createLoader,
  };
}

type CoordinatorQueries = ReturnType<
  typeof createCoordinatorQueriesFromOptions
>;

export function createCoordinatorQueries(
  baseUrl: string,
  onAuthError?: (error: unknown) => void
): CoordinatorQueries;
export function createCoordinatorQueries(
  options: CreateCoordinatorQueriesOptions
): CoordinatorQueries;
export function createCoordinatorQueries(
  optionsOrBaseUrl: CreateCoordinatorQueriesOptions | string,
  legacyOnAuthError?: (error: unknown) => void
): CoordinatorQueries {
  return createCoordinatorQueriesFromOptions(
    typeof optionsOrBaseUrl === "string"
      ? { baseUrl: optionsOrBaseUrl, onAuthError: legacyOnAuthError }
      : optionsOrBaseUrl
  );
}
